import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Link as LinkIcon,
  Add as AddIcon,
  Store as StoreIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchDashboardStats,
  fetchTransactions,
  fetchPaymentLinks,
} from '../../store/slices/merchantSlice';

const MerchantDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { dashboardStats, transactions, loadingDashboard, loadingTransactions } = useSelector(
    (state: RootState) => state.merchant
  );

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchTransactions({ limit: 10 }));
    dispatch(fetchPaymentLinks());
  }, [dispatch]);

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  const StatCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: string;
    color: string;
  }> = ({ icon, title, value, color }) => (
    <Card sx={{
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: 3,
      height: '100%',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#7F839C', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{
              fontWeight: 700,
              color: '#202647',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color, opacity: 0.8 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loadingDashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#202647',
          mb: 1,
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}>
          Merchant Dashboard
        </Typography>
        <Typography variant="body2" sx={{ color: '#7F839C' }}>
          Welcome back! Here's your business overview.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<TrendingUpIcon sx={{ fontSize: { xs: 28, md: 32 } }} />}
            title="Total Revenue"
            value={formatCurrency(dashboardStats?.totalRevenue || 0, dashboardStats?.currency || 'USD')}
            color="#11C1EC"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<AccountBalanceIcon sx={{ fontSize: { xs: 28, md: 32 } }} />}
            title="Available Balance"
            value={formatCurrency(dashboardStats?.availableBalance || 0, dashboardStats?.currency || 'USD')}
            color="#09486D"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<ReceiptIcon sx={{ fontSize: { xs: 28, md: 32 } }} />}
            title="Total Transactions"
            value={String(dashboardStats?.totalTransactions || 0)}
            color="#00C48C"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            icon={<LinkIcon sx={{ fontSize: { xs: 28, md: 32 } }} />}
            title="Active Links"
            value={String(dashboardStats?.activePaymentLinks || 0)}
            color="#FFB946"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ pb: 2 }}>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: '#202647',
            mb: 2,
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}>
            Quick Actions
          </Typography>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/merchant/payment-links')}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, md: 1.2 },
                }}
              >
                Create Payment Link
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<StoreIcon />}
                onClick={() => navigate('/merchant/store')}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, md: 1.2 },
                }}
              >
                Manage Store
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => navigate('/merchant/settings')}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, md: 1.2 },
                }}
              >
                Request Settlement
              </Button>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/merchant/transactions')}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, md: 1.2 },
                }}
              >
                View All Transactions
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Recent Transactions */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: '#202647',
            mb: 2,
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}>
            Recent Transactions
          </Typography>

          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={40} />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#7F839C' }}>
                No transactions yet. Create a payment link to get started!
              </Typography>
            </Box>
          ) : (
            <Box sx={{
              overflowX: 'auto',
              display: { xs: 'block', md: 'table-row-group' },
            }}>
              <TableContainer component={Paper} sx={{
                boxShadow: 'none',
                border: 'none',
                display: { xs: 'none', md: 'table' },
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.slice(0, 5).map((transaction) => (
                      <TableRow
                        key={transaction.id}
                        sx={{ '&:hover': { backgroundColor: '#F8F8FC' } }}
                      >
                        <TableCell sx={{ color: '#565972', fontSize: '0.9rem' }}>
                          {transaction.reference}
                        </TableCell>
                        <TableCell sx={{ color: '#565972', fontSize: '0.9rem', fontWeight: 600 }}>
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status}
                            color={getStatusColor(transaction.status) as any}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#7F839C', fontSize: '0.85rem' }}>
                          {new Date(transaction.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                {transactions.slice(0, 5).map((transaction) => (
                  <Card key={transaction.id} sx={{ p: 2, backgroundColor: '#F8F8FC' }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.75rem' }}>
                        Reference
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                        {transaction.reference}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.75rem' }}>
                          Amount
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </Typography>
                      </Box>
                      <Box>
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#7F839C' }}>
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </Typography>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {transactions.length > 0 && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/merchant/transactions')}
              sx={{ mt: 2 }}
            >
              View All Transactions
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MerchantDashboardPage;
